import type { Dispatch, RefObject, SetStateAction } from "react"
import { ArrowDown, ArrowUp, Eye, EyeOff } from "lucide-react"
import { toast } from "react-toastify"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export type ColumnOrderDragState = { id: string; position: "above" | "below" } | null

export type ComponentTableColumnOrderDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentColumnOrderDraft: string[]
  reorderableColumnIds: string[]
  defaultColumnOrderIds: string[]
  columnOrderScrollRef: RefObject<HTMLDivElement | null>
  draggingColumnId: string | null
  setDraggingColumnId: (id: string | null) => void
  dragOverState: ColumnOrderDragState
  setDragOverState: (state: ColumnOrderDragState) => void
  setColumnOrderDraft: Dispatch<SetStateAction<string[] | null>>
  moveColumnInDraft: (id: string, direction: "up" | "down") => void
  handleColumnDrop: (sourceId: string, targetId: string) => void
  onReset: () => void
  onSave: () => void
  columnHiddenDraft: string[]
  toggleColumnHiddenInDraft: (id: string) => void
  columnOrderVisibleCount: number
  fieldLabelByName?: Record<string, string>
}

export function ComponentTableColumnOrderDialog({
  open,
  onOpenChange,
  currentColumnOrderDraft,
  reorderableColumnIds,
  defaultColumnOrderIds,
  columnOrderScrollRef,
  draggingColumnId,
  setDraggingColumnId,
  dragOverState,
  setDragOverState,
  setColumnOrderDraft,
  moveColumnInDraft,
  handleColumnDrop,
  onReset,
  onSave,
  columnHiddenDraft,
  toggleColumnHiddenInDraft,
  columnOrderVisibleCount,
  fieldLabelByName,
}: ComponentTableColumnOrderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Columns</DialogTitle>
          <DialogDescription>
            Reorder fields and tap the eye icon to show or hide columns. This setting is saved in your browser.
          </DialogDescription>
        </DialogHeader>
        <div
          ref={columnOrderScrollRef}
          className="mt-2 space-y-2 max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-gray-50/50 dark:bg-gray-900/40"
        >
          {currentColumnOrderDraft.map((id) => {
            const isVisible = !columnHiddenDraft.includes(id)
            const label = fieldLabelByName?.[id] ?? id
            return (
              <div key={id} className="relative">
                {dragOverState && dragOverState.id === id && dragOverState.position === "above" && (
                  <div className="absolute -top-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full pointer-events-none" />
                )}
                <div
                  className={`flex items-center justify-between gap-2 px-2 py-1 rounded border ${draggingColumnId === id
                    ? "border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/40"
                    : "border-transparent hover:border-gray-200 dark:hover:border-gray-700 bg-white dark:bg-gray-800"
                    } text-sm cursor-move`}
                  draggable={reorderableColumnIds.includes(id)}
                  onDragStart={() => {
                    setDraggingColumnId(id)
                    setDragOverState(null)
                  }}
                  onDragOver={(event) => {
                    if (!draggingColumnId || draggingColumnId === id) return
                    event.preventDefault()
                    const container = columnOrderScrollRef.current
                    if (container) {
                      const containerRect = container.getBoundingClientRect()
                      const scrollUpThreshold = containerRect.top + 80
                      const scrollDownThreshold = containerRect.bottom - 40
                      if (event.clientY < scrollUpThreshold) {
                        container.scrollTop = container.scrollTop - 10
                      } else if (event.clientY > scrollDownThreshold) {
                        container.scrollTop = container.scrollTop + 10
                      }
                    }
                    const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect()
                    const offsetY = event.clientY - rect.top
                    const position: "above" | "below" =
                      offsetY < rect.height / 2 ? "above" : "below"
                    setDragOverState({ id, position })
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (draggingColumnId && draggingColumnId !== id) {
                      const position = dragOverState?.position || "below"
                      if (position === "above") {
                        handleColumnDrop(draggingColumnId, id)
                      } else {
                        handleColumnDrop(draggingColumnId, id)
                        setColumnOrderDraft((prev) => {
                          const base =
                            prev && prev.length === reorderableColumnIds.length
                              ? prev
                              : defaultColumnOrderIds
                          const list = [...base]
                          const fromIndex = list.indexOf(draggingColumnId)
                          const targetIndex = list.indexOf(id)
                          if (fromIndex === -1 || targetIndex === -1) return list
                          if (fromIndex > targetIndex) {
                            list.splice(fromIndex, 1)
                            list.splice(targetIndex + 1, 0, draggingColumnId)
                          }
                          return list
                        })
                      }
                    }
                    setDraggingColumnId(null)
                    setDragOverState(null)
                  }}
                  onDragLeave={(event) => {
                    const related = event.relatedTarget as HTMLElement | null
                    if (!related || !event.currentTarget.contains(related)) {
                      if (dragOverState && dragOverState.id === id) {
                        setDragOverState(null)
                      }
                    }
                  }}
                  onDragEnd={() => {
                    setDraggingColumnId(null)
                    setDragOverState(null)
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm ${isVisible ? "" : "text-gray-500 dark:text-gray-400 line-through"}`}
                    >
                      {label}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      draggable={false}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        moveColumnInDraft(id, "up")
                      }}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      draggable={false}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        moveColumnInDraft(id, "down")
                      }}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 ${isVisible ? "text-gray-700 dark:text-gray-200" : "text-gray-400 dark:text-gray-500"}`}
                      title={isVisible ? "Hide column" : "Show column"}
                      aria-label={isVisible ? `Hide column ${label}` : `Show column ${label}`}
                      draggable={false}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (isVisible && columnOrderVisibleCount <= 1) {
                          toast.error("Keep at least one column visible")
                          return
                        }
                        toggleColumnHiddenInDraft(id)
                      }}
                    >
                      {isVisible ? (
                        <Eye className="h-4 w-4" aria-hidden />
                      ) : (
                        <EyeOff className="h-4 w-4" aria-hidden />
                      )}
                    </Button>
                  </div>
                </div>
                {dragOverState && dragOverState.id === id && dragOverState.position === "below" && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full pointer-events-none" />
                )}
              </div>
            )
          })}
          {currentColumnOrderDraft.length === 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 px-1 py-2">
              No configurable columns available for this table.
            </div>
          )}
        </div>
        <DialogFooter className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
            {columnOrderVisibleCount} of {currentColumnOrderDraft.length} columns visible
          </p>
          <div className="flex w-full items-center justify-between gap-2 order-1 sm:order-2 sm:w-auto">
            <Button type="button" variant="outline" onClick={onReset} disabled={!reorderableColumnIds.length}>
              Reset to default
            </Button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={onSave} disabled={!reorderableColumnIds.length}>
                Save
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
